import * as React from "react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Button,
  FormLabel,
  Stack,
  TextField,
  FormHelperText,
} from "@mui/material";
import { makeStyles } from '@mui/styles';
import adminStyles from 'Styles/adminStyles';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import "dayjs/locale/da"; // Danish locale
dayjs.locale("da"); // Set global locale


export default function DateTimeRangePickerWithPresets({
  label = "Vælg dato & tidsinterval",
  initialFrom = null,
  initialTo = null,
  onChange,
  presets = [
    { label: "Seneste 7 dage", days: 7 },
    { label: "Seneste 30 dage", days: 30 },
    { label: "Seneste 90 dage", days: 90 },
  ],
  disabled = false,
  minRangeDays = 0, // Minimum range in days
  maxRangeDays = Infinity, // Maximum range in days
}) {
  const classes = adminStyles();
  const useStyles = makeStyles((theme) => ({
    button: {
      color: '#000 !important',
      backgroundColor: '#93FF50 !important',
    },
  }));

  const [from, setFrom] = useState(
    initialFrom ? dayjs(initialFrom) : dayjs().subtract(7, "day")
  );
  const [to, setTo] = useState(initialTo ? dayjs(initialTo) : dayjs());

  const lastEmittedRef = useRef({ from: null, to: null });

  // 🔹 Memoized validation
  const isValid = useMemo(() => {
    if (!from || !to || to.isBefore(from)) return false;
    const rangeDays = to.diff(from, "day");
    let valid = rangeDays >= minRangeDays && rangeDays <= maxRangeDays;
    onChange(lastEmittedRef.current, valid);
    return valid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, minRangeDays, maxRangeDays]);

  // 🔹 Only emit onChange if values changed
  useEffect(() => {
    if (!onChange || !isValid) return;

    const last = lastEmittedRef.current;
    const fromISO = from.toISOString();
    const toISO = to.toISOString();

    if (last.from !== fromISO || last.to !== toISO) {
      lastEmittedRef.current = { from: fromISO, to: toISO };
      onChange(lastEmittedRef.current, isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, isValid, onChange]);

  // 🔹 Preset handler
  const handlePreset = useCallback(
    (days) => {
      const newTo = dayjs();
      const newFrom = newTo.subtract(days, "day");

      if (!newFrom.isSame(from) || !newTo.isSame(to)) {
        setFrom(newFrom);
        setTo(newTo);
      }
    },
    [from, to]
  );

  // 🔹 Input change handlers
  const handleFromChange = (newValue) => {
    if (!newValue) return;

    let updatedFrom = newValue;
    if (to && updatedFrom.isAfter(to)) updatedFrom = to; // clamp
    if (to && updatedFrom.isBefore(to.subtract(maxRangeDays, "day")))
      updatedFrom = to.subtract(maxRangeDays, "day"); // enforce maxRange
    setFrom(updatedFrom);
  };

  const handleToChange = (newValue) => {
    if (!newValue) return;

    let updatedTo = newValue;
    if (from && updatedTo.isBefore(from)) updatedTo = from; // clamp
    if (from && updatedTo.isAfter(from.add(maxRangeDays, "day")))
      updatedTo = from.add(maxRangeDays, "day"); // enforce maxRange
    setTo(updatedTo);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {label && <FormLabel component="legend">{label}</FormLabel>}

        <Stack direction="row" spacing={2} mt={1} mb={0}>
          <DateTimePicker
            label="Fra"
            value={from}
            onChange={handleFromChange}
            disabled={disabled}
            ampm={false} // 24-hour format
            minDateTime={to?.subtract(maxRangeDays, "day")}
            maxDateTime={to}
            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
          />
          <DateTimePicker
            label="Til"
            value={to}
            onChange={handleToChange}
            disabled={disabled}
            ampm={false} // 24-hour format
            minDateTime={from}
            maxDateTime={from?.add(maxRangeDays, "day")}
            renderInput={(params) => (
              <TextField {...params} size="small" fullWidth error={!isValid} />
            )}
          />
        </Stack>

        {!isValid && (
          <FormHelperText error>
            {to.isBefore(from)
              ? `"To" date/time must be later than or equal to "From"`
              : `Range must be between ${minRangeDays} and ${maxRangeDays} days`}
          </FormHelperText>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
          {presets.map((p) => {
            // Only enable preset if it fits within maxRangeDays
            const isDisabled = p.days > maxRangeDays;
            return (
              <Button
                key={p.label}
                variant="contained"
                size="small"
                onClick={() => handlePreset(p.days)}
                disabled={disabled || isDisabled}
                color="admin"
                // style={{ color: '#000 !important', backgroundColor: '#93FF50 !important' }}
                className={classes.button}
              >
                {p.label}
              </Button>
            );
          })}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}